import { IIntegrationSetup } from "api/interfaces";
import { makeAutoObservable } from "mobx";
import { CompanyStore, ContractStore, MessageStore } from "../";
import {
  IBranch,
  IBudget,
  ICity,
  IClient,
  ICostCenter,
  IDocumentType,
  IFinancialIndicator,
  IIndicatorQuotation,
  IPaymentCondition,
  IProduct,
  IProject,
} from "../../api/interfaces/megaERP";
import MegaERPService from "../../api/services/MegaERPService";
import { lang } from "../../i18nLang";

class Store {
  private _api?: MegaERPService = undefined;
  private _authenticated: boolean = false;
  private _lastConnectionError?: Error = undefined;
  public branchs: IBranch[] = [];
  public clients: IClient[] = [];
  public loadingClients: boolean = false;
  public paymentConditions: IPaymentCondition[] = [];
  public documentTypes: IDocumentType[] = [];
  public costCenters: ICostCenter[] = [];
  public projectsErp: IProject[] = [];
  public budgets: IBudget[] = [];
  public productItems: IProduct[] = [];
  public citiesItems: ICity[] = [];
  public financialIndicators: IFinancialIndicator[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  get hasIntegration(): boolean {
    const data = CompanyStore.currentCompany?.integrationSetup;
    return Boolean(data && data.host);
  }

  get isAuthenticated(): boolean {
    return this._authenticated;
  }

  get api(): MegaERPService {
    if (!this._api) throw new Error("API not defined");
    return this._api;
  }

  private reset() {
    this._api = undefined;
    this._authenticated = false;
    this._lastConnectionError = undefined;
    this.branchs = [];
    this.clients = [];
    this.loadingClients = false;
    this.paymentConditions = [];
    this.documentTypes = [];
    this.costCenters = [];
    this.projectsErp = [];
    this.budgets = [];
    this.financialIndicators = [];
  }

  async getDataToContract() {
    if (this.hasIntegration)
      if (!this._authenticated || !this.branchs.length) {
        const error: any = this._lastConnectionError;
        MessageStore.error(
          error?.customMessage || lang("contract.errors.fetchData")
        );
        return;
      }

    const contract = ContractStore.getCurrentContract();
    if (contract) {
      const branchId = contract.branchId || 0;
      await this.fetchAndSetDocumentTypes(branchId);

      if (contract.development.customerId) {
        this.fetchAndSetClients(branchId);
      }
    }
  }

  async getDataToExecutiveBudget() {
    if (this.hasIntegration)
      if (!this._authenticated || !this.branchs.length) {
        const error: any = this._lastConnectionError;
        MessageStore.error(
          error?.customMessage || lang("contract.errors.fetchData")
        );
        return;
      }

    const contract = ContractStore.getCurrentContract();
    if (contract) {
      const branchId = contract.executiveBudget?.branchId || contract.branchId;
      await this.fetchAndSetBudgets(branchId || 0);
    }
  }

  findBranch(branchId: number): IBranch | undefined {
    return this.branchs.find((b) => b.id === branchId);
  }

  findBudget(budgetId: string): IBudget | undefined {
    return this.budgets.find((b) => b.id === budgetId);
  }

  findPaymentCondition({
    paymentCondition,
  }: any): IPaymentCondition | undefined {
    const paymentConditionId = paymentCondition?.id as string;
    return this.paymentConditions.find((pay) => pay.id === paymentConditionId);
  }

  findDocumentType({ documentType }: any): IDocumentType | undefined {
    const documentTypeId = documentType?.id as string;
    return this.documentTypes.find((doc) => doc.id === documentTypeId);
  }

  findCostCenter({ costCenter }: any): ICostCenter | undefined {
    const costCenterId = costCenter?.id as string;
    return this.costCenters.find((cost) => cost.id === costCenterId);
  }

  findProjectErp({ projectErp }: any): IProject | undefined {
    const projectId = projectErp?.id as string;
    return this.projectsErp.find((proj) => proj.id === projectId);
  }

  findFinancialIndicator(indicatorId: number): IFinancialIndicator | undefined {
    return this.financialIndicators.find((ind) => ind.id === indicatorId);
  }

  findCityByName(name: string): ICity | undefined {
    return this.citiesItems.find((city) => city.name === name);
  }

  async authenticateUser(integrationData?: IIntegrationSetup) {
    const data =
      integrationData || CompanyStore.currentCompany?.integrationSetup;

    if (data && data.host) {
      try {
        const oldApi = this._api;
        const newApi = new MegaERPService(
          data.host,
          data.user,
          data.password,
          data.tenantId
        );
        const authenticated = await newApi.getAuthToken();

        // Reseta as listas se as credencias forem diferentes
        if (!oldApi || oldApi.isDifferent(newApi)) {
          this.reset();
        }

        this._api = newApi;
        this._authenticated = authenticated;

        return authenticated;
      } catch (error: any) {
        console.error(error);
        this.reset();
        this._lastConnectionError = error;
        throw error;
      }
    }

    this.reset();
    return false;
  }

  async fetchMainListsOfERP() {
    if (!this._authenticated || this.branchs.length) return;

    const promises = [
      this.fetchAndSetBranchs(),
      this.fetchAndSetCostCenters(),
      this.fetchAndSetProjectsErp(),
      this.fetchAndSetPaymentConditions(),
      this.fetchAndSetFinancialIndicators(),
      this.fetchAndSetCitiesItems(),
    ];

    await Promise.all(promises);
  }

  async fetchAndSetClients(branchId: number) {
    this.clients = [];
    if (!this.isAuthenticated) return;
    if (!branchId && branchId !== 0) return;

    this.loadingClients = true;
    const clientsData = await this.api
      .getClient(branchId, "")
      .catch((error) => MessageStore.error(lang(`contract.errors.client`)))
      .finally(() => {
        this.loadingClients = false;
      });
    this.clients = clientsData || [];
  }

  async fetchAndSetDocumentTypes(branch?: number) {
    this.documentTypes = [];
    if (!this.isAuthenticated || !branch) return;

    const userId = this.api.getUserIdFromToken();
    const typesData = await this.api
      .getDocumentType(branch, userId)
      .catch((error) =>
        MessageStore.error(lang(`contract.errors.documentType`))
      );
    this.documentTypes = typesData || [];
  }

  async fetchAndSetBudgets(branchId?: number) {
    this.budgets = [];
    if (!this.isAuthenticated || !branchId) return;

    const budgetsData = await this.api
      .getBudget(branchId)
      .catch((error) => MessageStore.error(lang(`contract.errors.budget`)));
    this.budgets = budgetsData || [];
  }

  async fetchAndSetProductItems(documentTypeId: string) {
    this.productItems = [];
    if (!this.isAuthenticated || !documentTypeId) return;
    const documentType = this.findDocumentType({
      documentType: { id: documentTypeId },
    });
    const productItemData = await this.api
      .getProductItems(documentType?.groups || [])
      .catch((error) =>
        MessageStore.error(lang(`contract.errors.productItems`))
      );
    this.productItems = productItemData || [];
  }

  async fetchBudgetTotals(budgetId?: string) {
    if (this.isAuthenticated && budgetId) {
      try {
        const totals = await Promise.all([
          this.api.getBudgetTotal(budgetId),
          this.api.getBudgetAddedTotal(budgetId),
          this.api.getBudgetRealizedTotal(budgetId),
        ]);

        return {
          total: totals[0],
          addedTotal: totals[1],
          realizedTotal: totals[2],
        };
      } catch (error) {
        console.error(error);
      }
    }
    return { total: 0, addedTotal: 0, realizedTotal: 0 };
  }

  async fetchFinancialIndicatorQuotation(
    indicator: IFinancialIndicator,
    referencialDate: Date
  ): Promise<IIndicatorQuotation | undefined> {
    if (!this.isAuthenticated) return;

    return this.api
      .getFinancialIndicatorQuotation(indicator, referencialDate)
      .catch((error) => {
        MessageStore.error(
          error.customMessage ||
            lang(`contract.errors.finacialIndicatorQuotation`)
        );
        return undefined;
      });
  }

  async fetchBranchsCity(branchAgentId: string): Promise<string> {
    if (!this.isAuthenticated) return "";

    return this.api.getBranchsCity(branchAgentId).catch((error) => {
      MessageStore.error(error.customMessage || lang(`ERRO`));
      return "";
    });
  }

  private async fetchAndSetBranchs() {
    this.branchs = [];
    if (!this.isAuthenticated) return;
    const branchsData = await this.api
      .getActivatedBranches()
      .catch(() =>
        this.api
          .getBranches()
          .catch(() => MessageStore.error(lang(`contract.errors.branch`)))
      );
    this.branchs = branchsData || [];
  }

  private async fetchAndSetPaymentConditions() {
    this.paymentConditions = [];
    if (!this.isAuthenticated) return;
    const conditionsData = await this.api
      .getPaymentCondition()
      .catch((error) =>
        MessageStore.error(lang(`contract.errors.paymentCondition`))
      );
    this.paymentConditions = conditionsData || [];
  }

  private async fetchAndSetCostCenters() {
    this.costCenters = [];
    if (!this.isAuthenticated) return;
    const costData = await this.api
      .getCostCenter()
      .catch((error) => MessageStore.error(lang(`contract.errors.costCenter`)));
    this.costCenters = costData || [];
  }

  private async fetchAndSetProjectsErp() {
    this.projectsErp = [];
    if (!this.isAuthenticated) return;
    const projectData = await this.api
      .getProject()
      .catch((error) => MessageStore.error(lang(`contract.errors.projectErp`)));
    this.projectsErp = projectData || [];
  }

  private async fetchAndSetFinancialIndicators() {
    this.financialIndicators = [];
    if (!this.isAuthenticated) return;

    const indicatorsData = await this.api
      .getFinancialIndicators()
      .catch((error) =>
        MessageStore.error(lang(`contract.errors.finacialIndicators`))
      );

    this.financialIndicators = indicatorsData || [];
  }

  private async fetchAndSetCitiesItems() {
    this.citiesItems = [];
    if (!this.isAuthenticated) return;
    const citiesData = await this.api
      .getCities()
      .catch((error) => MessageStore.error(lang(`contract.errors.branch`)));
    this.citiesItems = citiesData || [];
  }
}

const ContractIntegrationStore = new Store();
export default ContractIntegrationStore;
